import type { V1AdminUser } from '../client/types.gen'

import { useLocalStorage } from '@vueuse/core'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { toast } from 'vue-sonner'

import { adminAuthServiceLogin } from '../client'
import { useApi } from '../composables/useApi'
import { errorToMessage } from '../utils/error'

// Define user type for admin platform
export interface User extends V1AdminUser {
  avatar?: string
}

// Define login credentials interface
export interface LoginCredentials {
  email: string
  password: string
  rememberMe?: boolean
}

// Define OAuth login credentials interface
export interface OAuthLoginCredentials {
  provider: 'OAUTH_PROVIDER_GITHUB' | 'OAUTH_PROVIDER_GOOGLE'
  code: string
  redirectUri?: string
}

export const useUserStore = defineStore('user', () => {
  const storageToken = useLocalStorage('user/token', '')
  const storageUser = useLocalStorage('user/info', '', {
    serializer: {
      read: (v: string) => (v ? JSON.parse(v) : undefined),
      write: (v: any) => JSON.stringify(v),
    },
  })

  const user = ref<User | undefined>(storageUser.value)
  const isAuthenticated = computed(() => !!storageToken.value)

  const login = async (credentials: LoginCredentials) => {
    try {
      const { data } = (await useApi(adminAuthServiceLogin, {
        body: {
          email: credentials.email,
          password: credentials.password,
        },
      })).expect('登录失败')

      const responseData = data as { adminUser?: V1AdminUser, token?: string }
      const userData = responseData.adminUser
      if (!userData) {
        throw new Error('用户数据不存在')
      }
      if (!responseData.token) {
        throw new Error('Token 不存在')
      }
      storageToken.value = responseData.token
      user.value = userData as User
      storageUser.value = userData as User

      return responseData
    }
    catch (error) {
      toast.error(errorToMessage(error))
      throw error
    }
  }

  // OAuth login not supported for admin users
  const oauthLogin = async (_credentials: OAuthLoginCredentials) => {
    throw new Error('OAuth 登录不支持管理员账户')
  }

  // Get current user info from stored data
  const me = async () => {
    // For admin users, we use the stored user data from login
    if (user.value && storageToken.value) {
      // Update avatar from gravatar if email is available
      if (user.value.email) {
        const emailHash = Array.from(
          new Uint8Array(
            await crypto.subtle.digest('SHA-256', new TextEncoder().encode(user.value.email)),
          ),
        ).map(b => b.toString(16).padStart(2, '0')).join('')
        user.value.avatar = `https://www.gravatar.com/avatar/${emailHash}?d=identicon`
      }
      return
    }
    throw new Error('未登录')
  }

  const logout = async (): Promise<void> => {
    try {
      // Clear state
      user.value = undefined
      storageToken.value = ''
      storageUser.value = ''
    }
    catch {
      // Silently handle logout errors
    }
  }

  return {
    isAuthenticated,
    storageToken,
    user,

    login,
    oauthLogin,
    me,
    logout,
  }
})
